import { Injectable } from '@angular/core';
import { Service, Storage, Terminal, Ticket } from '../models';
import { Template } from '../models/template.model';

@Injectable()
export class StorageService {
  private key = 'app.storage';
  private ticketKeyPrefix = 'app.ticket.';
  private storageAux = localStorage.getItem(this.key);

  constructor() {
    if (!this.storageAux) {
      this.initStorage();
    }
  }

  private get storage(): Storage {
    return JSON.parse(localStorage.getItem(this.key));
  }

  get currentTerminal(): Terminal {
    return this.storage.current.terminal;
  }

  get currentTicket(): Ticket {
    return this.storage.current.ticket;
  }

  get currentService(): Service {
    return this.storage.current.service;
  }

  get tickets(): Ticket[] {
    const tickets = new Map<number, Ticket>(this.storage.tickets.map(ticket => [ticket.id, ticket]));
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.ticketKeyPrefix)) {
        try {
          const ticket = JSON.parse(localStorage.getItem(key)) as Ticket;
          if (ticket?.id) tickets.set(ticket.id, ticket);
        } catch { /* Ignore invalid historical data. */ }
      }
    }
    return Array.from(tickets.values());
  }

  getTicketsForTerminal(accessKey: string): Ticket[] {
    return this.tickets.filter(ticket => ticket.terminalAccessKey === accessKey);
  }

  getTicketsForCurrentContext(): Ticket[] {
    return this.tickets.filter(ticket => this.isTicketInCurrentContext(ticket));
  }

  get currentTemplate(): Template {
    return this.storage.current.template;
  }

  get terminals(): Terminal[] {
    return this.storage.terminals;
  }

  initStorage(): void {
    const storage: Storage = {
      current: {
        terminal: null,
        service: null,
        ticket: null,
        template: null,
      },
      terminals: [],
      tickets: []
    };

    localStorage.setItem(this.key, JSON.stringify(storage));
  }

  clear(): void {
    this.tickets.forEach(ticket => localStorage.removeItem(this.ticketKeyPrefix + ticket.id));
    this.initStorage();
  }

  addTerminal(terminal: Terminal, accessKey?: string, setCurrent = true): void {
    const savedTerminal = { ...terminal, accessKey: accessKey || terminal.accessKey };
    const storage = this.storage;
    const index = storage.terminals.findIndex((t) => t.id === savedTerminal.id);

    if (index > -1) {
      storage.terminals[index] = savedTerminal;
    } else {
      storage.terminals.push(savedTerminal);
    }

    localStorage.setItem(this.key, JSON.stringify(storage));
    if (setCurrent) {
      this.setCurrentTerminal(savedTerminal);
    }
  }

  addTicket(ticket: Ticket): void {
    const storage = this.storage;
    const index = storage.tickets.findIndex((t) => t.id === ticket.id);
    const saved = this.tickets.find(t => t.id === ticket.id);
    ticket.terminalAccessKey ||= saved?.terminalAccessKey;

    if (index > -1) {
      storage.tickets[index] = ticket;
    } else {
      storage.tickets.push(ticket);
    }

    localStorage.setItem(this.key, JSON.stringify(storage));
    if (ticket.terminalAccessKey) {
      localStorage.setItem(this.ticketKeyPrefix + ticket.id, JSON.stringify(ticket));
    }
  }

  removeTerminal(id: number): void {
    const storage = this.storage;
    const index = storage.terminals.findIndex((t) => t.id === id);

    if (index === -1) {
      return;
    }

    storage.terminals.splice(index, 1);
    localStorage.setItem(this.key, JSON.stringify(storage));
  }

  removeTicket(id: number): void {
    const storage = this.storage;
    const index = storage.tickets.findIndex((t) => t.id === id);

    if (index > -1) {
      storage.tickets.splice(index, 1);
      localStorage.setItem(this.key, JSON.stringify(storage));
    }

    localStorage.removeItem(this.ticketKeyPrefix + id);
  }

  updateTicket(ticket: Ticket): void {
    const storage = this.storage;
    const index = storage.tickets.findIndex((t) => t.id === ticket.id);
    const saved = this.tickets.find(t => t.id === ticket.id);
    ticket.terminalAccessKey ||= saved?.terminalAccessKey;

    if (index === -1 && !saved) {
      return;
    }

    if (index > -1) storage.tickets[index] = ticket;
    localStorage.setItem(this.key, JSON.stringify(storage));
    if (ticket.terminalAccessKey) {
      localStorage.setItem(this.ticketKeyPrefix + ticket.id, JSON.stringify(ticket));
    }
  }

  unlinkTicketFromTerminal(id: number): void {
    const ticket = this.tickets.find(item => item.id === id);
    if (!ticket) return;

    const storage = this.storage;
    const unlinked = { ...ticket, terminalAccessKey: undefined };
    const index = storage.tickets.findIndex(item => item.id === id);
    if (index > -1) storage.tickets[index] = unlinked;
    else storage.tickets.push(unlinked);
    localStorage.setItem(this.key, JSON.stringify(storage));
    localStorage.removeItem(this.ticketKeyPrefix + id);
  }

  static isSameContext(a: Ticket, b: Ticket): boolean {
    return a.providerId === b.providerId
      && a.serviceConfigId === b.serviceConfigId;
  }

  setCurrentTicket(ticket: Ticket): void {
    const storage = this.storage;
    storage.current.ticket = ticket;
    localStorage.setItem(this.key, JSON.stringify(storage));

    if (ticket) {
      const terminal = this.findTerminal(ticket.providerId, ticket.locationId);

      if (terminal) {
        this.setCurrentTerminal(terminal);
      }
    } else {
      this.setCurrentTerminal(null);
    }

  }

  setCurrentService(service: Service) {
    const storage = this.storage;
    storage.current.service = service;
    localStorage.setItem(this.key, JSON.stringify(storage))
  }

  private setCurrentTerminal(terminal: Terminal): void {
    const storage = this.storage;
    storage.current.terminal = terminal;
    localStorage.setItem(this.key, JSON.stringify(storage));
  }

  private findTerminal(providerId: number, locationId: number): Terminal {
    return this.storage.terminals.find((t) => t.provider.id === providerId && t.location.id === locationId);
  }

  private isTicketInCurrentContext(ticket: Ticket): boolean {
    const currentTerminal = this.currentTerminal;
    const currentTicket = this.currentTicket;
    const providerId = currentTerminal?.provider.id ?? currentTicket?.providerId;
    const locationId = currentTerminal?.location.id ?? currentTicket?.locationId;

    if (providerId == null) {
      return false;
    }

    return ticket.providerId === providerId && (locationId == null || ticket.locationId === locationId);
  }

  setTemplate(template: Template) {
    const storage = this.storage;
    storage.current.template = template;
    localStorage.setItem(this.key, JSON.stringify(storage))
  }
}
